import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";

import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { FlavorFinderResult } from "../../types/FlavorFinderTypes";
import { FlavorFinderResetButton, FlavorFinderResultsButton } from "../Buttons/FlavorFinderButtons";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";
import { FlavorFinderResultCard } from "../ResultCard/FlavorFinderResultCard";
import { FlavorFinderResultSuggestionCard } from "../ResultSuggestionCard/FlavorFinderResultSuggestionCard";

import "./FlavorFinderResults.scss";

type FlavorFinderResultProps = {
  results: FlavorFinderResult;
};

export const FlavorFinderResults: FC<FlavorFinderResultProps> = ({ results }) => {
  const { resetQuiz, flavorFinderData } = useFlavorFinderContext();
  const { results: quizResults } = results;
  const { finalActionCtaLabel, finalActionCtaLink, redoCtaLabel, result } = flavorFinderData;
  const { title, subtitle, flavorLabel, intensityLabel, coolingLabel, deviceSuggestion } = flavorFinderData.result;
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const { isSmall } = useCurrentBreakpoint();

  const [useResultsWithDevice, setUseResultsWithDevice] = useState<string>("");

  const buttonUrl = useMemo(() => {
    const { resultsLinkWithDevice, resultsLink } = results;
    const deviceSuggestionCardEnabled = result.deviceSuggestionCardEnabled;

    const isUseResultsWithDeviceYes = useResultsWithDevice === "yes" || !useResultsWithDevice;
    const isUseResultsWithDeviceNo = useResultsWithDevice === "no";

    if (deviceSuggestionCardEnabled) {
      if (isUseResultsWithDeviceYes && resultsLinkWithDevice) {
        return resultsLinkWithDevice;
      }
      if (isUseResultsWithDeviceNo && resultsLink) {
        return resultsLink;
      }
    } else if (resultsLink) {
      return resultsLink;
    }

    return finalActionCtaLink || "#";
  }, [useResultsWithDevice, results, finalActionCtaLink]);

  const handleNextStepClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: finalActionCtaLabel,
      cta_location: "flavor-finder-results",
    });

    window.location.href = buttonUrl;
  };

  const handleSuggestionSelectionChange = (value: string) => {
    setUseResultsWithDevice(value);
  };

  const toggleActive = (index: number) => {
    setActiveIndex((prevIndex: number) => (prevIndex === index ? -1 : index));
  };

  useEffect(() => {
    dispatchTrackingEvent(TrackingEvents.TrackingFlavorFinderResult);
  }, []);

  return (
    <div className="flavorfinder__questions-wrapper" data-testid="flavor-finder-question">
      {isSmall && <FlavorFinderResetButton onClick={resetQuiz} label={redoCtaLabel} />}
      <div className={"flavorfinder__question-container"}>
        <div className="flavorfinder__question">
          <FlavorFinderHeader title={title} subtitle={subtitle}>
            {!isSmall && <FlavorFinderResetButton onClick={resetQuiz} label={redoCtaLabel} />}
          </FlavorFinderHeader>
          <div className="flavorfinder__results">
            {quizResults?.map((result, index) => (
              <FlavorFinderResultCard
                key={result.sku}
                result={result}
                flavorLabel={flavorLabel}
                intensityLabel={intensityLabel}
                coolingLabel={coolingLabel}
                isHiglighted={index === activeIndex}
                onClick={() => toggleActive(index)}
                index={index}
              />
            )) ?? null}
            {result.deviceSuggestionCardEnabled && (
              <FlavorFinderResultSuggestionCard
                description={deviceSuggestion.description}
                imgUrl={deviceSuggestion.imgUrl}
                useResultsWithDeviceLink={deviceSuggestion.defaultDeviceSuggestionState}
                onSelectionChange={handleSuggestionSelectionChange}
              />
            )}
          </div>
          <div className="flavorfinder__footer">
            <FlavorFinderResultsButton onClick={handleNextStepClick} label={finalActionCtaLabel} />
          </div>
        </div>
      </div>
    </div>
  );
};
